import { Prisma } from "@prisma/client";

let addressExtensions = {} as ReturnType<typeof getAddressExtensions>;

const getAddressExtensions = () => {
  return Prisma.defineExtension((client) => {
    type StudentOrGuardianId = Prisma.AtLeast<
      {
        studentId?: number;
        guardianId?: number;
      },
      "studentId" | "guardianId"
    >;

    type Args<A, S> = StudentOrGuardianId & Prisma.Exact<A, Omit<S, "where">>;

    return client.$extends({
      model: {
        address: {
          async findActive<
            T extends Prisma.AddressDelegate,
            A extends Omit<Prisma.AddressFindUniqueArgs, "where">,
          >(args: Args<A, Prisma.AddressFindUniqueArgs>) {
            const { studentId, guardianId, ...rest } =
              args as StudentOrGuardianId & A;

            const info = await client.address.findFirst({
              where: {
                ActiveStudent: { id: studentId },
                ActiveGuardian: { id: guardianId },
              },
              ...(rest as any),
            });

            return info as Prisma.Result<T, A, "findUnique">;
          },

          /**
           * Upsert an address record and set it as the active address for the student or guardian.
           * Doesn't actually upsert - always creates a new record.
           */
          async upsertActive<
            T extends Prisma.AddressDelegate,
            A extends Omit<Prisma.AddressUpsertArgs, "where">,
          >(args: Args<A, Prisma.AddressUpsertArgs>) {
            const { studentId, guardianId, ...rest } =
              args as StudentOrGuardianId & A;
            const active = await this.findActive({
              studentId,
              guardianId,
            });

            let address = null;
            const {
              update: updateData,
              create: createData,
              ...restRemaining
            } = rest;
            await client.$transaction(async (p) => {
              if (active) {
                // ----Update the active address record----
                const {
                  id: _,
                  createdAt: __,
                  updatedAt: ___,
                  ...oldDataToKeep
                } = active;
                // Create a new address record with the old data
                address = await (p.address as T).create({
                  data: {
                    ...oldDataToKeep,
                  },
                });
                // Then update it with the new data
                address = await (p.address as T).update({
                  where: { id: address.id },
                  data: {
                    ...updateData,
                  },
                  ...restRemaining,
                });
              } else {
                // ----Create a new address record----
                address = await (p.address as T).create({
                  data: {
                    ...createData,
                  } as Prisma.AddressCreateInput,
                  ...restRemaining,
                });
              }

              // Set this as client's active address
              if (studentId) {
                await p.student.update({
                  where: { id: studentId },
                  data: {
                    addressId: address.id,
                  },
                });
              }
              if (guardianId) {
                await p.guardian.update({
                  where: { id: guardianId },
                  data: {
                    addressId: address.id,
                  },
                });
              }
            });

            if (!address) {
              throw new Error("Address unable to be upserted!");
            }

            return address as Prisma.Result<T, A, "upsert">;
          },

          async createActive<
            T extends Prisma.AddressDelegate,
            A extends Omit<Prisma.AddressCreateArgs, "where">,
          >(args: Args<A, Prisma.AddressCreateArgs>) {
            const { studentId, guardianId, ...rest } =
              args as StudentOrGuardianId & A;
            const { data, ...restRemaining } = rest;
            const { studentId: _, guardianId: __, ...dataWithoutIds } = data;

            let address = null;
            await client.$transaction(async (p) => {
              address = await (p.address as T).create({
                data: {
                  ...dataWithoutIds,
                  Student: studentId
                    ? { connect: { id: studentId } }
                    : undefined,
                  Guardian: guardianId
                    ? { connect: { id: guardianId } }
                    : undefined,
                },
                ...restRemaining,
              } as Prisma.AddressCreateArgs);

              // Set this as client's active insurance info
              if (studentId) {
                await p.student.update({
                  where: { id: studentId },
                  data: { addressId: address.id },
                });
              }
              if (guardianId) {
                await p.guardian.update({
                  where: { id: guardianId },
                  data: { addressId: address.id },
                });
              }
            });

            if (!address) {
              throw new Error("Address unable to be created!");
            }

            return address as Prisma.Result<T, A, "create">;
          },
        },
      },
    });
  });
};

if (!process.browser) {
  addressExtensions = getAddressExtensions();
}

export default addressExtensions;
